import { categories } from "../services/catalogService";

/**
 * Normalize text by removing accents and lowering case.
 * Used for search matching and category comparison.
 */
export const normalizeText = (text: string | null | undefined): string =>
  (text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/**
 * Match a raw category string from the database to a known category ID.
 * Handles accent normalization, case-insensitive matching, and rosca auto-detection.
 *
 * @param rawCategory - The raw Categoría value from the database row
 * @param productId - The product ID (used for rosca auto-detection)
 * @param productName - The product name (used for rosca auto-detection)
 * @returns The matched category ID, or the cleaned raw string if no match
 */
export const matchCategory = (
  rawCategory: string,
  productId?: string,
  productName?: string
): string => {
  const cleanRaw = normalizeText(rawCategory);

  // Auto-detect Roscas by ID prefix or name
  if (
    productId?.startsWith("rosca-") ||
    (productName && normalizeText(productName).includes("rosca"))
  ) {
    return "roscas";
  }

  // Manejo de ediciones especiales
  if (
    cleanRaw === "edicion especial (campana 2)" ||
    cleanRaw === "edicion especial 2" ||
    cleanRaw === "edicion-especial-2" ||
    cleanRaw === "temporada 2"
  ) {
    return "edicion-especial-2";
  }

  if (
    cleanRaw === "edicion especial" ||
    cleanRaw === "edicion especial (campana 1)" ||
    cleanRaw === "edicion-especial" ||
    cleanRaw === "especial" ||
    cleanRaw === "temporada"
  ) {
    return "edicion-especial";
  }

  const matchedCategory = categories.find((cat) => {
    const cleanName = normalizeText(cat.name);
    const cleanId = normalizeText(cat.id);
    return cleanRaw === cleanName || cleanRaw === cleanId;
  });

  return matchedCategory ? matchedCategory.id : cleanRaw;
};

/**
 * Check if a category is the "special edition" seasonal category.
 */
export const isSpecialEdition = (category: string): boolean => {
  const normalized = normalizeText(category);
  return (
    normalized === "edicion-especial" ||
    normalized === "edicion especial" ||
    normalized === "edicion-especial-2" ||
    normalized === "edicion especial 2" ||
    normalized.includes("edicion especial") ||
    normalized.includes("edición especial")
  );
};

/**
 * Sort products: custom order first, then roscas first, then by default logic.
 */
export const sortProducts = (a: any, b: any): number => {
  // 1. Custom order (orden field)
  const hasOrdenA = a.orden !== undefined && a.orden !== null;
  const hasOrdenB = b.orden !== undefined && b.orden !== null;

  if (hasOrdenA && hasOrdenB) return a.orden! - b.orden!;
  if (hasOrdenA && !hasOrdenB) return -1;
  if (!hasOrdenA && hasOrdenB) return 1;

  // 2. Roscas first
  const isRoscaA = a.id?.startsWith("rosca-") || normalizeText(a.name).includes("rosca");
  const isRoscaB = b.id?.startsWith("rosca-") || normalizeText(b.name).includes("rosca");

  if (isRoscaA && !isRoscaB) return -1;
  if (isRoscaB && !isRoscaA) return 1;
  if (isRoscaA && isRoscaB) return a.name.localeCompare(b.name);

  // 3. Arreglo Floral first
  if (a.id === "arreglo-floral-main") return -1;
  if (b.id === "arreglo-floral-main") return 1;

  return 0;
};
